/**
 * Data formatting utilities
 */

import type { TrustScore } from '../types';

/**
 * Format TrustScore to percentage string
 */
export function formatTrustScore(score: number | TrustScore): string {
  if (typeof score === 'number') {
    const percent = score <= 1 ? score * 100 : score;
    return `${Math.round(percent)}%`;
  }
  
  if (score && typeof score.totalScore === 'number') {
    const percent = score.totalScore <= 1 ? score.totalScore * 100 : score.totalScore;
    return `${Math.round(percent)}%`;
  }
  
  return '0%';
}

/**
 * Get trust score color based on value
 */
export function getTrustScoreColor(score: number): string {
  if (score <= 30) return '#ef4444'; // Red
  if (score <= 60) return '#f59e0b'; // Yellow
  if (score <= 80) return '#84cc16'; // Light green
  return '#22c55e'; // Green
}

/**
 * Get trust score label based on value
 */
export function getTrustScoreLabel(score: number): string {
  if (score <= 30) return 'Baja fiabilidad';
  if (score <= 60) return 'Fiabilidad media';
  if (score <= 80) return 'Fiable';
  return 'Muy fiable';
}

/**
 * Format date to relative time
 */
export function formatRelativeTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 7) {
    return dateObj.toLocaleDateString();
  }
  if (diffDays > 0) return `hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
  if (diffHours > 0) return `hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
  if (diffMins > 0) return `hace ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
  return 'hace un momento';
}

/**
 * Truncate text to max length
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Highlight query terms in text
 */
export function highlightText(text: string, query: string): string {
  if (!query || !text) return text;
  
  const terms = query.split(/\s+/).filter(t => t.length > 2);
  let result = text;
  
  terms.forEach(term => {
    const regex = new RegExp(`(${escapeRegex(term)})`, 'gi');
    result = result.replace(regex, '<mark>$1</mark>');
  });
  
  return result;
}

/**
 * Escape regex special characters
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function(...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Get favicon URL from domain
 */
export function getFaviconUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`;
  } catch {
    return '';
  }
}
